import { JSX } from 'react'

import ContactUsSection from '@/components/ContactUsSection'
import Link from '@/components/Link'
import SocialIcon, { ISocialIcon } from '@/components/SocialIcon'

import {
  discordUrl,
  privacyPolicyUrl,
  twitterUrl,
  youtubeUrl,
  linkedinUrl,
  githubDatachainUrl,
  datachainDocsUrl,
  datachainQuickStartUrl,
  studioSignUpUrl
} from '@/constants/externalUrls'
import {
  // aboutPageCareersLink,
  blogsPageLink,
  pricingPageLink,
  overviewSectionLink,
  securityAndPrivacyPageLink
} from '@/constants/internalLinks'
import { ReactComponent as DataChainSVG } from '@/static/images/logos/datachain.svg'
import { ReactComponent as GithubSVG } from '@/static/images/social/github.svg'
import { cn } from '@/utils'

interface IFooterLinkData {
  href: string
  text: string | JSX.Element
  icon?: JSX.Element
  target?: '_blank'
}

interface IFooterListData {
  header: string
  links: Array<IFooterLinkData>
}

const styles = {
  icon: `h-6 w-6 flex-shrink-0`,
  productIcon: `flex-shrink-0 mr-2.5 w-4 h-4 md:w-5 md:h-5`,
  columns: `flex flex-row gap-2 justify-between w-full flex-wrap`,
  column: `flex flex-col mt-3 gap-2 px-2`,
  heading: `text-xs font-semibold text-zinc-500 uppercase`,
  links: `flex flex-col gap-1`,
  link: `text-base font-light text-zinc-400 hover:text-zinc-100 flex flex-row items-center`,
  socialIcons: `flex gap-3 md:gap-6 items-center`,
  socialIcon: `w-4 h-4 md:w-6 md:h-6`,
  logo: `w-4 h-4`,
  companyLogo: `inline-block w-4 h-4 mr-1.5`
}

const footerListsData: Array<IFooterListData> = [
  {
    header: `Product`,
    links: [
      {
        href: overviewSectionLink,
        text: `Overview`
      },
      {
        href: pricingPageLink,
        text: `Pricing & Features`
      },
      {
        href: githubDatachainUrl,
        text: `GitHub repository`,
        icon: <GithubSVG className={styles.productIcon} />
      },
      {
        href: studioSignUpUrl,
        text: `DataChain Studio`,
        icon: <DataChainSVG className={styles.productIcon} />
      }
    ]
  },
  {
    header: `Help`,
    links: [
      { href: datachainDocsUrl, text: `Documentation` },
      { href: datachainQuickStartUrl, text: `Quick Start` },
      { href: discordUrl, text: `Discord` }
    ]
  },
  {
    header: `Company`,
    links: [
      {
        href: blogsPageLink,
        text: `Blog`
      },
      {
        text: `Security and Privacy`,
        href: securityAndPrivacyPageLink
      },
      {
        href: privacyPolicyUrl,
        text: `Privacy Policy`
      }
    ]
  }
]

const footerSocialIconsData: Array<ISocialIcon> = [
  {
    site: `github`,
    label: `DataChain GitHub`,
    url: githubDatachainUrl
  },
  {
    site: `twitter`,
    label: `DataChain X`,
    url: twitterUrl
  },
  {
    site: `youtube`,
    label: `DVC.org YouTube Channel`,
    url: youtubeUrl
  },
  {
    site: `linkedinNoBg`,
    label: `DataChain LinkedIn`,
    url: linkedinUrl
  },
  {
    site: `discord`,
    label: `DVC Discord chat`,
    url: discordUrl
  }
]

const FooterLists = () => (
  <div className={cn(styles.columns, `mt-auto`)}>
    {footerListsData.map(({ header, links }, index) => (
      <div className={styles.column} key={index}>
        <div className={styles.heading}>{header}</div>
        <ul className={styles.links}>
          {links.map(({ text, target, href, icon }, i) => (
            <li
              // className={styles.linkItem}
              key={i}
            >
              <Link target={target} href={href} className={styles.link}>
                {icon}
                {text}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
)

const FooterSocialIcons = () => (
  <div className={styles.socialIcons}>
    {footerSocialIconsData.map(({ site, label, url }, index) => (
      <SocialIcon
        key={index}
        site={site}
        label={label}
        url={url}
        className={cn(
          styles.link,
          styles.socialIcon,
          `transition-transform hover:scale-125`
        )}
      />
    ))}
  </div>
)

const Footer = () => (
  <div className="mt-auto">
    <ContactUsSection />
    <footer className="bg-slate-900">
      <div className="container py-6">
        <div className="flex flex-col lg:flex-row">
          <div className="mt-3 lg:mr-24">
            <Link href="/" title="DataChain" className="flex items-center">
              <DataChainSVG className="h-6 w-6" />
              <span className="ml-2 whitespace-nowrap">DataChain, Inc.</span>
            </Link>
          </div>
          <FooterLists />
        </div>
        <div
          className={`flex flex-col items-center justify-around gap-6 py-4 text-center md:flex-row md:text-left`}
        >
          <FooterSocialIcons />
        </div>
      </div>
    </footer>
  </div>
)

export default Footer
